(() => {
  const LAUNCH_SELECTOR = '[data-bst-salesforce-chat-launch]';
  const LOADER_SELECTOR = '[data-bst-salesforce-chat-loader]';
  const READY_EVENT = 'bst:salesforce-chat:ready';
  const ESW_SCRIPT_URLS = [
    'https://d1u00000147avuaq.my.salesforce.com/embeddedservice/5.0/esw.min.js',
    'https://service.force.com/embeddedservice/5.0/esw.min.js',
  ];
  const CHAT_OPEN_SELECTORS = [
    '.embeddedServiceSidebar',
    '.embeddedServiceSidebar.modalContainer',
    '.embeddedMessagingFrame',
    '.embeddedMessagingConversationButton',
  ];
  const LOADING_TIMEOUT_MS = 20000;

  let chatReady = false;
  let chatLoading = false;
  let openWhenReady = false;
  let scriptLoadAttempt = 0;
  let activeLaunchButton = null;
  let loadingTimeoutId = null;
  let sidebarObserver = null;

  const getEmbeddedSvc = () => window.embedded_svc;

  const setButtonLoading = (button) => {
    if (!button || button.classList.contains('is-loading')) return;

    activeLaunchButton = button;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const loader = button.querySelector(LOADER_SELECTOR);
    if (loader) {
      loader.setAttribute('aria-hidden', 'false');
    }
  };

  const clearButtonLoading = () => {
    if (loadingTimeoutId) {
      window.clearTimeout(loadingTimeoutId);
      loadingTimeoutId = null;
    }

    if (sidebarObserver) {
      sidebarObserver.disconnect();
      sidebarObserver = null;
    }

    if (!activeLaunchButton) return;

    const button = activeLaunchButton;
    activeLaunchButton = null;

    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    button.disabled = false;

    const loader = button.querySelector(LOADER_SELECTOR);
    if (loader) {
      loader.setAttribute('aria-hidden', 'true');
    }
  };

  const isChatOpenInDom = () =>
    CHAT_OPEN_SELECTORS.some((selector) => {
      const node = document.querySelector(selector);
      if (!node) return false;

      const style = window.getComputedStyle(node);
      const visible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        node.getClientRects().length > 0;

      return visible;
    });

  const watchForChatOpen = (onOpen) => {
    if (isChatOpenInDom()) {
      onOpen();
      return;
    }

    sidebarObserver = new MutationObserver(() => {
      if (isChatOpenInDom()) {
        onOpen();
      }
    });

    sidebarObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
    });
  };

  const startLoadingSession = (button) => {
    setButtonLoading(button);

    watchForChatOpen(() => {
      clearButtonLoading();
    });

    loadingTimeoutId = window.setTimeout(() => {
      clearButtonLoading();
    }, LOADING_TIMEOUT_MS);
  };

  const initESW = (gslbBaseURL) => {
    if (window.__bstSalesforceChatGlobalInit) {
      chatReady = Boolean(window.__bstSalesforceChatGlobalReady);
      return;
    }

    const embedded_svc = getEmbeddedSvc();
    if (!embedded_svc) return;

    embedded_svc.settings.displayHelpButton = true;
    embedded_svc.settings.language = '';
    embedded_svc.settings.defaultMinimizedText = 'Live Chat';
    embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    embedded_svc.settings.entryFeature = 'LiveAgent';

    embedded_svc.addEventHandler('onSettingsCallCompleted', () => {
      chatReady = true;
      document.dispatchEvent(new CustomEvent(READY_EVENT));

      if (openWhenReady) {
        openWhenReady = false;
        openChat();
      }
    });

    embedded_svc.addEventHandler('onChatEstablished', () => {
      clearButtonLoading();
    });

    embedded_svc.addEventHandler('onChatRequestSuccess', () => {
      clearButtonLoading();
    });

    embedded_svc.addEventHandler('onChatRequestFailed', () => {
      clearButtonLoading();
    });

    try {
      embedded_svc.init(
        'https://d1u00000147avuaq.my.salesforce.com',
        'https://d1u00000147avuaq.my.site.com/',
        gslbBaseURL,
        '00D1U00000147aV',
        'Customer_Service',
        {
          baseLiveAgentContentURL: 'https://c.la1-core2.sfdc-lywfpd.salesforceliveagent.com/content',
          deploymentId: '5721U000000VCot',
          buttonId: '573Uk000000051J',
          baseLiveAgentURL: 'https://d.la1-core2.sfdc-lywfpd.salesforceliveagent.com/chat',
          eswLiveAgentDevName: 'Customer_Service',
          isOfflineSupportEnabled: true,
        }
      );
    } catch {
      clearButtonLoading();
    }
  };

  const appendScript = (url) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.body.appendChild(script);
    });

  const loadESW = async () => {
    if (getEmbeddedSvc()) {
      if (window.__bstSalesforceChatGlobalReady || chatReady) {
        chatReady = true;
        return;
      }

      await new Promise((resolve) => {
        document.addEventListener(READY_EVENT, resolve, { once: true });
      });
      chatReady = true;
      return;
    }

    if (chatLoading) {
      await new Promise((resolve) => {
        document.addEventListener(READY_EVENT, resolve, { once: true });
      });
      return;
    }

    chatLoading = true;

    while (scriptLoadAttempt < ESW_SCRIPT_URLS.length) {
      const url = ESW_SCRIPT_URLS[scriptLoadAttempt];
      scriptLoadAttempt += 1;

      try {
        await appendScript(url);
        const gslbBaseURL = url.includes('service.force.com') ? 'https://service.force.com' : null;
        initESW(gslbBaseURL);
        return;
      } catch {
        // Try next script URL
      }
    }

    chatLoading = false;
    throw new Error('All Salesforce Embedded Service script URLs failed to load');
  };

  const clickHiddenHelpButton = () => {
    const selectors = [
      '.embeddedServiceHelpButton .helpButton',
      '.embeddedServiceHelpButton button',
      '.helpButtonEnabled',
      '.embeddedServiceHelpButton',
    ];

    for (const selector of selectors) {
      const node = document.querySelector(selector);
      if (node) {
        node.click();
        return true;
      }
    }

    return false;
  };

  const openChat = () => {
    const embedded_svc = getEmbeddedSvc();
    if (!embedded_svc) {
      clearButtonLoading();
      return false;
    }

    if (typeof embedded_svc.onHelpButtonClick === 'function') {
      try {
        embedded_svc.onHelpButtonClick();
        return true;
      } catch {
        // Fall back to clicking the hidden launcher
      }
    }

    return clickHiddenHelpButton();
  };

  const handleLaunchClick = async (event) => {
    const button = event.currentTarget;

    if (button.classList.contains('is-loading')) return;

    startLoadingSession(button);

    try {
      await loadESW();

      if (chatReady) {
        openChat();
        return;
      }

      openWhenReady = true;

      window.setTimeout(() => {
        if (!chatReady) {
          openChat();
        }
      }, 5000);
    } catch {
      clearButtonLoading();
    }
  };

  const bindLaunchers = (root = document) => {
    root.querySelectorAll(LAUNCH_SELECTOR).forEach((button) => {
      if (button.dataset.bstSalesforceChatBound === 'true') return;

      button.dataset.bstSalesforceChatBound = 'true';
      button.addEventListener('click', handleLaunchClick);
    });
  };

  const init = () => {
    document.documentElement.classList.add('bst-salesforce-chat-active');
    bindLaunchers();

    if (window.__bstSalesforceChatGlobalInit) {
      if (window.__bstSalesforceChatGlobalReady) {
        chatReady = true;
      }
      return;
    }

    loadESW().catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    bindLaunchers(event.target);
  });
})();
